import useSWR from 'swr';
import { IndexedDBFileStore, FileHandle } from '@/lib/engine/core/file-store';

export interface LocalDataset {
  id: string;
  name: string;
  file: File;
  size: string;
  lastModified: string;
}

// Helper function to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' bytes';
  else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  else if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  else return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
};

const fetchLocalDatasets = async () => {
  // Only fetch in browser environment
  if (typeof window === 'undefined') {
    return [];
  }
  
  const fileStore = new IndexedDBFileStore();
  const files = await fileStore.listFileHandles();
  await fileStore.close();
  
  // Transform to our LocalDataset format
  return files.map(({ name, fileHandle }) => {
    const file = fileHandle.handle;
    return {
      id: name,
      name: file.name,
      file,
      size: formatFileSize(file.size),
      lastModified: new Date(file.lastModified).toLocaleDateString()
    };
  });
};

export function useLocalDatasets() {
  const { data: localDatasets, error, isLoading, mutate } = useSWR<LocalDataset[]>(
    'local-datasets',
    fetchLocalDatasets,
    {
      keepPreviousData: true,
      revalidateOnFocus: false,
      dedupingInterval: 1000
    }
  );

  const removeLocalDataset = async (datasetId: string) => {
    try {
      const fileStore = new IndexedDBFileStore();
      await fileStore.deleteFile(datasetId);
      await fileStore.close();
      await mutate(); // Trigger a revalidation
      return { success: true };
    } catch (error) {
      console.error('Error removing file:', error);
      return { success: false, error };
    }
  };

  return {
    localDatasets: localDatasets || [],
    isLoading,
    error,
    removeLocalDataset,
    refresh: mutate
  };
} 